import { Popover } from '@base-ui/react/popover';
import styled from 'styled-components';

import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

// Day cells are 40px × 40px; 7 columns make the grid 280px wide. The popup
// surface adds POPUP_PADDING_PX on each side.
export const DAY_CELL_PX = 40;
const POPUP_PADDING_PX = 16;
export const GRID_WIDTH_PX = DAY_CELL_PX * 7;
const POPUP_WIDTH_PX = GRID_WIDTH_PX + POPUP_PADDING_PX * 2;

const calendarSurfaceStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: pxToRem(8),
  borderRadius: pxToRem(8),
  backgroundColor: theme.color.backgroundLightDark,
  color: theme.color.textPrimary,
  outline: 'none',
};

export const CalendarSurface = styled.div<{ $inline?: boolean }>((p) => ({
  ...calendarSurfaceStyle,
  // Inline drops the padding and shadow (the host menu/page provides those),
  // so the surface shrinks to just the grid width — keeps it from overflowing
  // narrow filter menus.
  width: pxToRem(p.$inline ? GRID_WIDTH_PX : POPUP_WIDTH_PX),
  padding: p.$inline ? 0 : pxToRem(POPUP_PADDING_PX),
  boxShadow: p.$inline ? 'none' : theme.shadow.medium,
}));

// z-index lives on the Positioner — the element floating-ui sets
// `position: absolute` on. Popover.Popup is a statically positioned <div>, so a
// z-index there has no effect; without it here the popover paints behind any
// positioned ancestor (e.g. Sidebar at z-index 9990).
export const CalendarPositioner = styled(Popover.Positioner)({
  zIndex: 10000,
});

// Sits above app chrome (e.g. Sidebar at z-index 9990) but below the positioner
// (10000) so the calendar itself stays clickable while the backdrop intercepts
// every other click to dismiss. Transparent — it captures clicks, it doesn't
// dim the page.
export const CalendarBackdrop = styled(Popover.Backdrop)({
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
});

export const CalendarPopup = styled(Popover.Popup)({
  ...calendarSurfaceStyle,
  width: pxToRem(POPUP_WIDTH_PX),
  padding: pxToRem(POPUP_PADDING_PX),
  boxShadow: theme.shadow.medium,
});

// Cell-state styles shared between react-day-picker's day buttons (applied via
// the .rdp-* selectors in DayPickerWrapper) and our own month/year GridCells,
// so the three grids can't drift apart visually.
// Hover and keyboard-focus treatment for an enabled cell. The inset box-shadow
// draws a focus ring without changing the cell's layout, and pairs with the
// hover background so the cell reads as both focused and "active" without a
// default browser outline.
export const interactiveCellStyle = {
  ':hover': {
    backgroundColor: theme.color.uiTrans5,
  },
  ':focus-visible': {
    backgroundColor: theme.color.uiTrans5,
    boxShadow: `inset 0 0 0 2px ${theme.color.elementBlue100}`,
  },
};
// Solid fill for the committed selection (and today, when nothing is selected).
export const selectedCellStyle = {
  backgroundColor: theme.color.elementBlue100,
  color: theme.color.white100,
  font: theme.font.bold14,
  ':hover': {
    backgroundColor: theme.color.elementBlue100,
  },
};
export const disabledCellStyle = {
  backgroundColor: theme.color.uiTrans5,
  color: theme.color.textBullet,
  borderRadius: 0,
  cursor: 'not-allowed',
  ':hover': {
    backgroundColor: theme.color.uiTrans5,
  },
};
